import { find } from 'lodash'
import LocationMapPOIService from '@/services/LocationMapPOIService'
import LocationMapService from '@/services/LocationMapService'
import InfoWindowMixin from '@/mixins/InfoWindowMixin'

export default {
  mixins: [InfoWindowMixin],

  props: {
    poiParentAttribute: String,
    poiParentId: String,
  },
  data() {
    return {
      poiTypes: Object,
      deletedPois: [],
      poiMap: new Map(),
      activePoiTypes: [],
      poiMarkers: new Map(),
    }
  },
  methods: {
    initPointsOfInterest() {
      this.map.data.setStyle((poi) => {
        return {
          icon: poi.getProperty('icon'),
        }
      })
    },

    async loadPointsOfInterest(data) {
      const modifiedRecords = data.records
      await LocationMapPOIService.getPointsOfInterest(
        this.poiParentAttribute,
        this.poiParentId,
      ).then((response) => {
        if (response.data) {
          // filter out deleted records
          const features = response.data.features.filter((feature) => {
            return !modifiedRecords.some((record) => {
              const wasDeleted = record.status === 'deleted' && record.id === feature.properties.id
              if (wasDeleted) {
                this.deletedPois.push(feature.properties.id)
              }
              return wasDeleted
            })
          })
          // restore pending records
          modifiedRecords
            .filter((record) => {
              return record.status === 'added' || record.status === 'edited'
            })
            .forEach((record) => {
              const iconUrl = this.getIconUrl(record.iconName)
              this.addPointOfInterest(record.id, record.position, iconUrl)
            })

          this.createMarkers(features)
        }
      })
    },
    async loadGlobalPointsOfInterest(poiType) {
      await LocationMapPOIService.getGlobalPointsOfInterest(this.map.getBounds(), poiType).then(
        (response) => {
          if (response.data) {
            const features = response.data.features.filter((feature) => {
              return !this.poiMarkers.has(feature.id) && !this.deletedPois.includes(feature.id)
            })

            this.createMarkers(features)
            if (poiType) {
              if (this.poiMap.has(poiType)) {
                const pois = this.poiMap.get(poiType)
                features.forEach((feature) => {
                  if (!pois.includes(feature)) {
                    pois.push(feature)
                  }
                })
                this.poiMap.set(poiType, pois)
              } else {
                this.poiMap.set(poiType, features)
              }
            }
          }
        },
      )
      if (poiType && !this.activePoiTypes.includes(poiType)) {
        this.activePoiTypes.push(poiType)
      }
    },

    createMarkers(features) {
      features.forEach((feature) => {
        if (this.poiMarkers.has(feature.id)) {
          return
        }

        const pos = {
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
        }
        const isDraggable = feature.properties.isEditable
        this.addPointOfInterest(feature.id, pos, feature.properties.icon, isDraggable)
        const poiMarker = this.poiMarkers.get(feature.id)
        this.addInfoWindowClickEvent(feature.id, poiMarker)
      })
    },

    addInfoWindowClickEvent(id, marker) {
      marker.addListener('click', () => {
        LocationMapService.getSummary(id, 'obj.com.bmssolutions.COMPONENT.bms_MapoiCo_001').then(
          (summary) => this.openInfoWindow(summary.data, marker),
        )
      })
    },

    loadActivatedGlobalPointsOfInterest() {
      this.activePoiTypes.forEach((poiType) => {
        this.loadGlobalPointsOfInterest(poiType)
      })
    },

    clearGlobalPointsOfInterest(poiType) {
      const poisToRemove = this.poiMap.get(poiType)
      poisToRemove.forEach((poi) => {
        const poiMarker = this.poiMarkers.get(poi.id)
        poiMarker.setMap(null)
        this.poiMarkers.delete(poi.id)
      })
      this.poiMap.delete(poiType)
      this.activePoiTypes.splice(this.activePoiTypes.indexOf(poiType), 1)
    },

    async loadPointsOfInterestTypes() {
      await LocationMapPOIService.getPointsOfInterestTypes().then((response) => {
        if (response.data) {
          this.poiTypes = response.data
        }
      })
    },
    async linkGlobalPOI(id) {
      await LocationMapPOIService.linkGlobalPOI(id, this.poiParentAttribute, this.poiParentId).then(
        (response) => {
          if (response.data) {
            // do nothing
          }
        },
      )
    },
    initializePOIEventListners() {
      this.registerListener('pois-load', (event) => {
        this.loadPointsOfInterest(event.data)
        this.map.addListener('idle', () => this.loadGlobalPointsOfInterest(''))
      })
      this.registerListener('poi-saved', (event) => {
        const iconUrl = this.getIconUrl(event.data.iconName)
        this.addPointOfInterest(event.data.id, event.data.position, iconUrl)
      })
      this.registerListener('poi-edited', (event) => {
        this.editPointOfInterest(event.data)
      })
    },
    editPointOfInterest(data) {
      const iconUrl = this.getIconUrl(data.iconName)
      const pos = {
        lat: data.position.lat,
        lng: data.position.lng,
      }
      const poiMarkerEdited = this.poiMarkers.get(data.id)
      poiMarkerEdited.setPosition(pos)
      poiMarkerEdited.setIcon(iconUrl)
    },
    addPointOfInterest(id, pos, iconUrl, isDraggable) {
      if (typeof isDraggable === 'undefined') {
        isDraggable = true
      }
      if (this.isDashboardMap()) {
        isDraggable = false
      }

      const poiMarker = new this.google.maps.Marker({
        position: pos,
        icon: iconUrl,
        draggable: isDraggable,
      })
      poiMarker.setMap(this.map)
      if (!this.isDashboardMap()) {
        poiMarker.addListener('rightclick', (event) => {
          this.$refs.mapContext.onRightClick(event, id, poiMarker)
        })
      }
      if (isDraggable) {
        poiMarker.addListener('dragend', (event) => {
          this.$refs.mapContext.poiDraggable(event, id, poiMarker)
        })
      }
      this.poiMarkers.set(id, poiMarker)
    },
    getIconUrl(iconName) {
      const poiTypeObj = find(this.poiTypes, (poiType) => poiType.name === iconName)
      if (poiTypeObj) {
        return poiTypeObj.iconUrl === '' ? undefined : poiTypeObj.iconUrl
      }
      return undefined
    },
  },
}
